import { QuizAnswer } from '../quiz-api';
import { ConflictHandler, RefetchOptions, MergeOptions } from '../../../graphql/conflictResolution/types';

/**
 * Fields that should never be modified by merge (immutable or system-managed)
 */
const IMMUTABLE_FIELDS = ['id', 'ownerIdAndWsId', 'quizAndUserId', 'quizId', 'userId', '__typename'];

/**
 * Fields that should always come from current state (system-managed)
 * CRITICAL: Uses version field (PostgreSQL), not version
 */
const SYSTEM_FIELDS = ['version', 'updatedAt'];

/**
 * Conflict handler for QuizAnswer entities.
 * Handles version conflicts during quiz answer updates with intelligent merging.
 * 
 * CRITICAL: QuizAnswer uses version field (PostgreSQL), same as Task tables.
 */
export const quizAnswerConflictHandler: ConflictHandler<QuizAnswer> = {
    entityType: 'QuizAnswer',

    async refetch({ entityId, dataClientService, authService }: RefetchOptions): Promise<QuizAnswer> {
        // Fallback refetch - server should provide currentState
        throw new Error(
            'QuizAnswer refetch not implemented - server should provide currentState in conflict response'
        );
    },

    merge({ currentState, intendedChanges }: MergeOptions<QuizAnswer>): Partial<QuizAnswer> {
        const merged: Partial<QuizAnswer> & Record<string, any> = {};

        // Start with current state
        Object.assign(merged, currentState);

        // Apply intended changes, respecting field categories
        for (const key in intendedChanges) {
            if (IMMUTABLE_FIELDS.includes(key) || SYSTEM_FIELDS.includes(key)) {
                continue;
            }
            if ((intendedChanges as any)[key] !== undefined) {
                (merged as any)[key] = (intendedChanges as any)[key];
            }
        }

        // Ensure version is from current state (critical for retry)
        (merged as any).version = (currentState as any).version;

        return merged;
    },

    maxRetries: 3,

    shouldRetry(conflict, attemptNumber): boolean {
        // Don't retry if entity was deleted
        if (conflict.conflictType === 'ENTITY_DELETED') {
            return false;
        }
        // Don't retry beyond max attempts
        return attemptNumber < 3;
    }
};
